import { c as _c } from "react/compiler-runtime";
import { FloatingPortal } from "@floating-ui/react";
import { autoUpdate, flip, size as floatingSize, offset, useFloating } from "@floating-ui/react-dom";
import { ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions, Field, Combobox as HeadlessCombobox, Label, Transition } from "@headlessui/react";
import { Down } from "@icon-park/svg";
import { logger } from "@justhome/common";
import { Place, useSearchPlaces } from "@justhome/api-core";
import { useDebouncedCallback } from "@justhome/common/hooks";
import { ErrorMessage, Icon, InfoIcon, Loader } from "@justhome/react-ui";
import useLanguage from "@lib/hooks/useLanguage";
import clsx from "clsx";
import { Fragment, useState } from "react";
import { Control, FieldValues, Path, UseControllerProps, useController } from "react-hook-form";
interface MapsComboboxProps {
  label?: string;
  info?: string;
  className?: string;
  emptyMessage?: string;
  placeholder?: string;
}
interface ControlledMapsComboboxProps<T extends FieldValues> extends Omit<MapsComboboxProps, "value" | "onChange" | "onBlur"> {
  control: Control<T>;
  name: Path<T>;
  rules?: UseControllerProps<T>["rules"];
}
const MapsCombobox = t0 => {
  const $ = _c(53);
  const {
    className,
    control,
    name,
    rules,
    label,
    placeholder,
    info,
    emptyMessage
  } = t0;
  let t1;
  if ($[0] !== control || $[1] !== name || $[2] !== rules) {
    t1 = {
      name,
      control,
      rules
    };
    $[0] = control;
    $[1] = name;
    $[2] = rules;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    field: t2,
    fieldState: t3
  } = useController(t1);
  const {
    value: fieldValue,
    onChange
  } = t2;
  const {
    error
  } = t3;
  const [value, setValue] = useState(fieldValue);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const [suggestions, setSuggestions] = useState(t4);
  const {
    mutateAsync: searchPlaces,
    isLoading
  } = useSearchPlaces();
  const locale = useLanguage();
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      placement: "bottom-start",
      strategy: "fixed",
      whileElementsMounted: autoUpdate,
      middleware: [offset(8), flip(), floatingSize({
        apply(t6) {
          const {
            availableHeight,
            elements,
            availableWidth
          } = t6;
          const rect = elements.reference.getBoundingClientRect();
          Object.assign(elements.floating.style, {
            maxHeight: `${Math.min(availableHeight, 400) - 16}px`,
            maxWidth: `${availableWidth - 16}px`,
            width: `${rect.width}px`
          });
        }
      })]
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const {
    x,
    y,
    refs: t6,
    strategy
  } = useFloating(t5);
  const {
    setReference,
    setFloating
  } = t6;
  let t7;
  if ($[6] !== locale || $[7] !== searchPlaces) {
    t7 = async query => {
      ;
      try {
        const newSearchResults = await searchPlaces({
          body: {
            text: query,
            lng: locale
          }
        });
        setSuggestions(newSearchResults);
      } catch (t8) {
        const e = t8;
        logger.error("error searching for places ", e);
      }
    };
    $[6] = locale;
    $[7] = searchPlaces;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const debouncedSearch = useDebouncedCallback(t7, 300);
  let t8;
  if ($[9] !== debouncedSearch) {
    t8 = e_0 => {
      const query_0 = e_0.target.value;
      debouncedSearch(query_0);
    };
    $[9] = debouncedSearch;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const handleSearch = t8;
  let t9;
  if ($[11] !== onChange) {
    t9 = place => {
      setValue(place);
      onChange(place?.id ?? null);
    };
    $[11] = onChange;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const handleSelect = t9;
  let t10;
  if ($[13] !== className) {
    t10 = clsx("flex flex-col w-full max-w-[360px]", className);
    $[13] = className;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== info || $[16] !== label) {
    t11 = label && <Label className="mb-xs flex text-title-1">{label}{info && <InfoIcon>{info}</InfoIcon>}</Label>;
    $[15] = info;
    $[16] = label;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== placeholder || $[19] !== setReference || $[20] !== value) {
    t12 = <ComboboxButton as={Fragment}>{t13 => {
        const {
          open
        } = t13;
        return <button ref={ref => {
          if (ref) {
            ref.tabIndex = 0;
          }
          setReference(ref);
        }} className={clsx("focusable w-full flex border transition-colors ease-out items-center border-neutral-50 text-neutral-575 hover:border-neutral-100 focus-within:border-neutral-100 text-button-1 m:text-body-2 rounded-s px-s")}><span className="grow text-left py-[11px] rounded-s truncate">{!value && placeholder && <span className="text-neutral-150">{placeholder}</span>}{value?.formattedAddress}</span><Icon className={clsx("ml-xs flex", {
            "rotate-180": open
          })} aria-hidden="true" icon={Down} size={24} /></button>;
      }}</ComboboxButton>;
    $[18] = placeholder;
    $[19] = setReference;
    $[20] = value;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const t13 = y ?? 0;
  const t14 = x ?? 0;
  let t15;
  if ($[22] !== strategy || $[23] !== t13 || $[24] !== t14) {
    t15 = {
      position: strategy,
      top: t13,
      left: t14
    };
    $[22] = strategy;
    $[23] = t13;
    $[24] = t14;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  let t17;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = clsx("flex items-center grow focus:outline-none bg-[inherit] placeholder:text-neutral-150 border-b border-b-neutral-50 mb-2xs text-button-1 m:text-body-2 rounded-t-s px-s");
    t17 = <Icon type="Search" className="mr-2xs text-neutral-150" />;
    $[26] = t16;
    $[27] = t17;
  } else {
    t16 = $[26];
    t17 = $[27];
  }
  let t18;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = clsx("grow focus:outline-none bg-[inherit] text-[inherit] placeholder:text-neutral-150 py-[11px]");
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== handleSearch) {
    t19 = <div className={t16}>{t17}<ComboboxInput autoFocus={true} onChange={handleSearch} className={t18} /></div>;
    $[29] = handleSearch;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] !== emptyMessage || $[32] !== isLoading || $[33] !== suggestions) {
    t20 = isLoading ? <div className="flex items-center justify-center py-m"><Loader size="md" /></div> : <>{suggestions.length === 0 && <div className="text-center py-s text-body-2">{emptyMessage}</div>}<ComboboxOptions className="focus:outline-none overflow-auto w-full">{suggestions.map(_temp)}</ComboboxOptions></>;
    $[31] = emptyMessage;
    $[32] = isLoading;
    $[33] = suggestions;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] !== setFloating || $[36] !== t15 || $[37] !== t19 || $[38] !== t20) {
    t21 = <FloatingPortal><Transition as="div" enter="transition ease-out duration-100" enterFrom="transform opacity-0" enterTo="transform opacity-100" leave="transition ease-in duration-75" leaveFrom="transform opacity-100" leaveTo="transform opacity-0" ref={setFloating} style={t15} className="z-[51] mt-2 w-fit rounded-md bg-neutral-0 focus:outline-none rounded-s shadow-3 pb-2xs flex flex-col">{t19}{t20}</Transition></FloatingPortal>;
    $[35] = setFloating;
    $[36] = t15;
    $[37] = t19;
    $[38] = t20;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== handleSelect || $[41] !== name || $[42] !== t12 || $[43] !== t21 || $[44] !== value) {
    t22 = <HeadlessCombobox name={name} immediate={true} as="div" className="relative flex w-full" value={value} onChange={handleSelect}>{t12}{t21}</HeadlessCombobox>;
    $[40] = handleSelect;
    $[41] = name;
    $[42] = t12;
    $[43] = t21;
    $[44] = value;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== error) {
    t23 = error && <ErrorMessage>{error.message}</ErrorMessage>;
    $[46] = error;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== t10 || $[49] !== t11 || $[50] !== t22 || $[51] !== t23) {
    t24 = <Field className={t10}>{t11}{t22}{t23}</Field>;
    $[48] = t10;
    $[49] = t11;
    $[50] = t22;
    $[51] = t23;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  return t24;
};
export default MapsCombobox;
function _temp(suggestion) {
  return <ComboboxOption key={suggestion.id} value={suggestion} className={clsx("group cursor-pointer px-s py-2xs w-full flex justify-between items-center hover:bg-neutral-12 data-[selected]:bg-neutral-12 data-[focus]:bg-neutral-25 text-body-2")}><div className="flex flex-col grow">{suggestion.displayName}<span className="text-app-body-2 text-neutral-425">{suggestion.formattedAddress}</span></div></ComboboxOption>;
}